import { logResults } from '@lune/common/logger'
import { CircularProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api as apiAlias } from '../api/alias'
import { api as apiGroup } from '../api/group'
import { type Group } from '../api/types'
import NewOrMoveAlias, { type ActionResult } from './aliasManage'

interface AliasMoveProps {
  alias: string
}

const AliasMove: React.FC<AliasMoveProps> = ({ alias }) => {
  const [ready, setReady] = useState<boolean>(false)
  const [group, setGroup] = useState<Group>()
  const [targets, setTargets] = useState<string[]>([])

  useEffect(() => {
    void apiGroup.fetch
      .single(alias)
      .then(logResults('move/group/get'))
      .then((result) => {
        setReady(true)
        setGroup(result)
        setTargets(result.members)
      })
      .catch((err: Error) => {
        console.error('Failed to fetch group for alias', alias, err)
        setReady(true)
      })
  }, [alias])

  const moveAlias: (
    newAlias: string,
    newRecipients: string[],
    selectedCandidate: Group,
    createNewGroup: boolean
  ) => Promise<ActionResult> = async (
    newAlias,
    newRecipients,
    selectedCandidate,
    createNewGroup
  ) => {
    const fullAlias = `${newAlias}@lunenetworks.com`

    if (group === undefined) {
      return {
        effect: 'errored',
        effectOn: '',
        severity: 'error',
        message: 'Group not set'
      }
    }

    return await apiAlias
      .item({ group: group.email, alias: fullAlias })
      .methods.move.patch({
        id: group.email,
        alias: fullAlias,
        to: selectedCandidate.email,
        newGroup: createNewGroup
          ? {
              name: selectedCandidate.name,
              email: selectedCandidate.email,
              members: newRecipients
            }
          : undefined
      })
      .then<ActionResult>(() => ({
        effect: 'moved',
        effectOn: fullAlias,
        from: group.email,
        severity: 'info',
        message: 'Move request received'
      }))
      .catch<ActionResult>((err: Error) => ({
        effect: 'errored',
        effectOn: fullAlias,
        severity: 'error',
        message: err.message
      }))
  }

  return ready && group !== undefined ? (
    <NewOrMoveAlias
      action="Move"
      alias={alias.split('@')[0]}
      targets={targets}
      selectedCandidate={group}
      title="Move Email Alias"
      targetGroupLabel="Move to:"
      onAction={moveAlias}
    />
  ) : ready && group === undefined ? (
    <div>Group not found for alias {alias}</div>
  ) : (
    <CircularProgress />
  )
}

export const AliasMoveWrapper: React.FC = () => {
  const [searchParams] = useSearchParams()
  const alias = searchParams.get('alias')

  console.log(alias)

  if (alias === null) {
    throw new Error('Missing alias')
  }

  return <AliasMove alias={alias} />
}
